// @ts-nocheck
import React, { useEffect, useRef, useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogActions, DialogContent, Grid } from "@mui/material";
import { Box, Stack, Button, TextField, Typography, Icons, DateTimeStandard } from '@curves/ui-base';
import { ProductInfo, GradientColorHeader, ReadTextField } from '@curves/ui-shared';
// TODO: 改為庫存與 icoach 共用的 EmployeeSelect 後統一匯入路徑
import { EmployeeSelect } from "@icoach/components";
import { callRef, isFunction, refIsRequiredError } from "@util/utils";
import { getNowTime, parseDate } from "@util/moment";
import useClubInventoryApi from "@apis/useClubInventoryApi";
import useGetEmployeeOptionState from "@util/hook/useGetEmployeeOptionState";
import { useSnackbar } from "notistack";

// 功能：商品庫存數量調整 Dialog
const AdjustStockQtyDialog = ({ open, onClose, sourceData = null, refresh }) => {
	// 表單欄位 ref：建單人、調整後數量、原因
	const creatorRef = useRef(null);
	const adjustedQtyRef = useRef(null);
	const reasonRef = useRef(null);
	const { employeeOption } = useGetEmployeeOptionState();
	const [detail, setDetail] = useState(null);
	const { enqueueSnackbar } = useSnackbar();

	// API：讀取明細、送出調整
	const { getClubInventoryDetailApi, putClubInventoryQuantityAdjustmentApi } = useClubInventoryApi();

	const productID = sourceData?.productID != null ? Number(sourceData.productID) : null;
	const variantID = sourceData?.variantID != null ? Number(sourceData.variantID) : 0;

	// 開啟且具備 productID 時取得庫存明細
	useEffect(() => {
		// 若 open / productID / variantID 變更導致 effect 重跑，舊的請求回來時不應覆蓋新狀態
		let isCancelled = false;

		const fetchDetail = async () => {
			if (!open || productID == null) {
				setDetail(null);
				return;
			}
			try {
				const resp = await getClubInventoryDetailApi({ productID, variantID });
				if (isCancelled) return;
				setDetail(resp ?? null);
			} catch (e) {
				if (!isCancelled) setDetail(null);
			}
		};

		fetchDetail();
		return () => {
			isCancelled = true;
		};
	}, [open, productID, variantID, getClubInventoryDetailApi]);

	// 顯示資料：detail 欄位 keyname 已由介面統一，這裡直接取值
	const productInfo = detail
		? {
				sku: detail.sku ?? "",
				name: detail.productName ?? "",
				spec: detail.variantName ?? "",
				imageUrl: detail.pictureSrc ?? "",
			}
		: null;
	const originalQty = Number(detail?.quantity ?? 0);
	const todayDisplayDate = parseDate(new Date(), DateTimeStandard.DisplayDateBySlash);

	// 關閉：交由父層控制 open；本層 detail 會在 open=false 時自動清空
	const handleClose = useCallback(e => isFunction(onClose) && onClose(e), [onClose]);

	// 送出：驗證後呼叫 putClubInventoryQuantityAdjustmentApi
	const handleSubmit = async () => {
		// 必填驗證（跟 CreatePurchaseDialog 一致：統一用 requiredRefs + refIsRequiredError）
		const requiredRefs = [creatorRef, adjustedQtyRef, reasonRef];
		if (refIsRequiredError(...requiredRefs)) return;

		// 調整後數量格式驗證：整數且 >= 0（不合法要給出警告提示）
		const adjustedQtyRaw = callRef(adjustedQtyRef, "getResult") || "";
		const adjustedQty = Number(adjustedQtyRaw);
		const isAdjustedQtyValid = adjustedQtyRaw !== "" && !Number.isNaN(adjustedQty) && Number.isInteger(adjustedQty) && adjustedQty >= 0;
		if (!isAdjustedQtyValid) {
			enqueueSnackbar("請輸入有效的調整後數量（整數且 >= 0）", { variant: "warning" });
			return;
		}

		const reasonText = String(callRef(reasonRef, "getResult") || "").trim();

		// 後端 ClubInventoryAdjustmentRequestModel 所需欄位
		const payload = {
			productID,
			variantID,
			employeeID: callRef(creatorRef, "getResult"),
			adjustmentDate: getNowTime(),
			targetQuantity: adjustedQty,
			reason: reasonText,
		};

		const resp = await putClubInventoryQuantityAdjustmentApi(payload);
		if (resp) {
			if (isFunction(refresh)) refresh();
			handleClose();
		}
	};

	// 未開啟不渲染，避免 ref 狀態與表單內容殘留在畫面外
	if (!open) return null;

	return (
		<Dialog className="simple-content dialog" PaperProps={{ className: "wx-65rem" }} open={open} onClose={handleClose} fullWidth maxWidth="sm">
			<GradientColorHeader onClose={handleClose}>庫存數量調整</GradientColorHeader>
			<DialogContent className="p-3">
				{productID == null ? null : !detail ? (
					<Typography color="textSecondary">無法取得庫存明細</Typography>
				) : (
					<Box component="div" className="adjust-stock-qty-form">
						<Grid container spacing={3} alignItems="flex-start">
							{/* 左側：商品資訊 */}
							<Grid item xs={4} className="adjust-stock-qty-form-product">
								<ProductInfo product={productInfo} layout="column" imageSize="auto" />
							</Grid>

							{/* 右側：表單欄位 */}
							<Grid item xs={8} className="adjust-stock-qty-form-fields">
								<Grid container spacing={2}>
									<Grid item xs={6}>
										<ReadTextField label="建單日期">{todayDisplayDate}</ReadTextField>
									</Grid>
									<Grid item xs={6}>
										<EmployeeSelect label="建單人" ref={creatorRef} options={employeeOption} fullWidth required />
									</Grid>
									<Grid item xs={12}>
										<Stack direction="row" spacing={2} alignItems="flex-start" className="adjust-stock-qty-form-qty-row">
											<ReadTextField label="原數量" className="adjust-stock-qty-form-original-qty">
												{originalQty}
											</ReadTextField>
											<span className="adjust-stock-qty-form-arrow">
												<Icons.ArrowUp className="adjust-stock-qty-form-arrow-icon" />
											</span>
											<div className="adjust-stock-qty-form-adjusted-qty">
												<TextField
													label="調整後數量"
													ref={adjustedQtyRef}
													defaultValue={detail?.lastInventoryCount ?? 0}
													type="number"
													required
													fullWidth
													inputProps={{ min: 0, step: 1 }}
												/>
											</div>
										</Stack>
									</Grid>
									<Grid item xs={12}>
										<TextField label="原因" ref={reasonRef} required placeholder="請說明庫存調整原因" multiline minRows={3} fullWidth />
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				{detail && (
					<Button variant="contained" onClick={handleSubmit}>
						確認
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};

AdjustStockQtyDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	sourceData: PropTypes.shape({
		productID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		variantID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	}),
	refresh: PropTypes.func,
};

AdjustStockQtyDialog.displayName = "AdjustStockQtyDialog";

export default AdjustStockQtyDialog;
